if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        this.variantUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, function(data){
          if(data){
            const variantPrice = data.data.variant.price;
            if(variantPrice && document.querySelector('.buy-button_price')){
              document.querySelector('.buy-button_price').innerHTML = theme.Currency.formatMoney(variantPrice, window.shopSettings.moneyFormat);
            }
          }
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/*
  Sticky add-to-cart bar.

  The real, in-flow `.product-form__buttons_qty` is what becomes fixed, so the
  native form submit (and any app that intercepts it, e.g. Kaching Bundles)
  keeps working untouched.

  Because that element leaves the flow when it goes fixed, an anchor of the
  exact same height is kept in its place. Without it the page collapses by the
  bar's height at the moment it sticks, and the trigger geometry moves with it —
  which is what produced the jump/flicker once Kaching Bundles injected its
  ~480px deal block above the buy buttons after load.

  The anchor is also the intersection target: its box is identical in both
  states, so the sticky threshold can never oscillate.
*/
(() => {
  const BAR_SELECTOR = '.product-form__buttons_qty';
  const ANCHOR_CLASS = 'sticky-atc-anchor';
  const STICKY_CLASS = 'is-sticky';
  const MOBILE_QUERY = '(max-width: 767px)';

  class StickyAddToCart {
    constructor() {
      this.bar = null;
      this.anchor = null;
      this.observer = null;
      this.resizeObserver = null;
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.measureFrame = null;
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.sync = this.sync.bind(this);
    }

    // Kaching Bundles mutates the buy-button area heavily while it renders;
    // coalesce so we reflow once per frame instead of once per mutation.
    scheduleMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    }

    // Attach to whatever buy-button bar is currently in the DOM. Safe to call
    // repeatedly — app embeds and Shopify section renders both replace nodes.
    attach() {
      const bar = document.querySelector(BAR_SELECTOR);
      if (!bar || bar === this.bar) return;

      this.detachObservers();
      this.bar = bar;

      // Reuse an anchor left over from a previous attach, otherwise create one.
      let anchor = bar.parentElement;
      if (!anchor || !anchor.classList.contains(ANCHOR_CLASS)) {
        anchor = document.createElement('div');
        anchor.className = ANCHOR_CLASS;
        anchor.setAttribute('aria-hidden', 'true');
        bar.parentNode.insertBefore(anchor, bar);
        anchor.appendChild(bar);
      }
      this.anchor = anchor;

      // Pin only once the bar has scrolled off the TOP of the viewport. A plain
      // !isIntersecting test is also true while the bar is still below the fold,
      // which would show the sticky CTA before the real button is ever reached.
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          const viewportTop = entry.rootBounds ? entry.rootBounds.top : 0;
          const scrolledPast = entry.boundingClientRect.bottom <= viewportTop;
          this.setSticky(!entry.isIntersecting && scrolledPast);
        },
        { threshold: 0 }
      );
      this.observer.observe(anchor);

      // Kaching Bundles hides the theme quantity input and swaps button copy,
      // both of which change the bar's height after it is already pinned.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(bar);
      }

      this.mediaQuery.addEventListener('change', this.onBreakpointChange);
      this.measure();
    }

    detachObservers() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
      this.observer = null;
      this.resizeObserver = null;
    }

    setSticky(sticky) {
      if (!this.bar || !this.anchor) return;
      const shouldStick = sticky && this.mediaQuery.matches;
      if (shouldStick === this.anchor.classList.contains(STICKY_CLASS)) return;

      // Reserve the space before pinning so the content below never moves.
      if (shouldStick) this.measure();
      this.anchor.classList.toggle(STICKY_CLASS, shouldStick);
      this.bar.classList.toggle(STICKY_CLASS, shouldStick);
    }

    // Only meaningful while the bar is still in the flow — once pinned it is
    // full-bleed and taller, which is not the space that needs reserving.
    measure() {
      if (!this.bar || !this.anchor) return;
      if (this.anchor.classList.contains(STICKY_CLASS)) return;
      const marginBottom = parseFloat(getComputedStyle(this.bar).marginBottom) || 0;
      const height = this.bar.getBoundingClientRect().height + marginBottom;
      if (height) this.anchor.style.setProperty('--sticky-atc-height', `${height}px`);
    }

    onBreakpointChange() {
      if (!this.mediaQuery.matches) this.setSticky(false);
      else this.measure();
    }

    // Re-attach when the bar itself is replaced; re-measure when anything around
    // it changes height. Kaching Bundles renders asynchronously and, on variant
    // change, re-renders — neither fires a scroll event.
    watch() {
      const container = document.querySelector('.product__info-container') || document.body;
      new MutationObserver(() => {
        this.attach();
        this.scheduleMeasure();
      }).observe(container, { childList: true, subtree: true });

      document.addEventListener('kaching-bundles-block-loaded', this.sync);
      document.addEventListener('shopify:section:load', this.sync);
      window.addEventListener('pageshow', this.sync);
    }

    sync() {
      this.attach();
      this.scheduleMeasure();
    }
  }

  const init = () => {
    const sticky = new StickyAddToCart();
    sticky.attach();
    sticky.watch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
